import { Graphics } from "pixi.js";

import { Colors } from "../../config/DesignConstants";
import BaseEntity from "../../core/entity/BaseEntity";
import { V } from "../../core/Vector";
import { VectorButton } from "../graphics/VectorButton";
import { VectorSlider } from "../graphics/VectorSlider";
import { TextAnchor, VectorText, VerticalAnchor } from "../text/VectorText";
import MainMenu from "./MainMenu";

export default class PauseMenu extends BaseEntity {
  pausable = false; // Continue to work even when paused
  tags = ["pauseMenu"];

  private background: Graphics;
  private titleText: VectorText;
  private volumeLabel: VectorText;
  private volumePercentage: VectorText;
  private resumeButton: VectorButton;
  private mainMenuButton: VectorButton;
  private instructionText: VectorText;
  private volumeSlider: VectorSlider;

  private currentVolume: number;

  constructor() {
    super();

    // Load saved volume or use default
    const savedVolume = localStorage.getItem("gameVolume");
    this.currentVolume = savedVolume !== null ? parseFloat(savedVolume) : 0.5;

    // Create background
    this.background = new Graphics();
    (this.background as any).layerName = "menuBackground";

    // Create title
    this.titleText = new VectorText({
      text: "PAUSED",
      scale: 5.0,
      strokeWidth: 3,
      color: Colors.CYAN,
      anchor: TextAnchor.CENTER,
      verticalAnchor: VerticalAnchor.CENTER,
      layer: "menu",
    });

    // Create volume label
    this.volumeLabel = new VectorText({
      text: "VOLUME",
      scale: 2.0,
      strokeWidth: 2,
      color: Colors.WHITE,
      anchor: TextAnchor.LEFT,
      verticalAnchor: VerticalAnchor.CENTER,
      layer: "menu",
    });

    // Create volume percentage display
    this.volumePercentage = new VectorText({
      text: `${Math.round(this.currentVolume * 100)}%`,
      scale: 2.0,
      strokeWidth: 2,
      color: Colors.CYAN,
      anchor: TextAnchor.RIGHT,
      verticalAnchor: VerticalAnchor.CENTER,
      layer: "menu",
    });

    // Create volume slider
    this.volumeSlider = new VectorSlider({
      value: this.currentVolume,
      min: 0,
      max: 1,
      step: 0.1,
      width: 200,
      onChange: this.handleVolumeChange,
      layer: "menu",
    });

    // Create resume button
    this.resumeButton = new VectorButton({
      text: "RESUME",
      style: "primary",
      size: "large",
      onClick: this.handleResume,
      layer: "menu",
    });

    // Create main menu button
    this.mainMenuButton = new VectorButton({
      text: "MAIN MENU",
      style: "secondary",
      size: "large",
      onClick: this.handleMainMenu,
      layer: "menu",
    });

    // Create instruction text
    this.instructionText = new VectorText({
      text: "PRESS ESC TO RESUME",
      scale: 1.5,
      strokeWidth: 2,
      color: Colors.WHITE,
      anchor: TextAnchor.CENTER,
      verticalAnchor: VerticalAnchor.CENTER,
      layer: "menu",
    });
  }

  private handleVolumeChange = (newVolume: number) => {
    this.currentVolume = newVolume;

    // Update game master gain
    if (this.game?.masterGain) {
      this.game.masterGain.gain.value = newVolume;
    }

    // Save to localStorage
    localStorage.setItem("gameVolume", newVolume.toString());

    // Update percentage display
    this.volumePercentage.text = `${Math.round(newVolume * 100)}%`;
  };

  private handleResume = () => {
    this.game?.unpause();
    this.destroy();
  };

  private handleMainMenu = () => {
    if (!this.game) return;

    // Store game reference before destroying anything
    const game = this.game;

    // Use setTimeout to ensure this happens on the next frame
    // This prevents race conditions with other event handlers
    setTimeout(() => {
      // Destroy all pause menus manually
      const pauseMenus = game.entities.getTagged("pauseMenu") || [];
      for (const pauseMenu of pauseMenus) {
        pauseMenu.destroy();
      }

      // Destroy the game flow manager (and all game entities)
      const gameFlowManager = game.entities.getById("gameFlowManager");
      if (gameFlowManager) {
        gameFlowManager.destroy();
      }

      // Add main menu
      game.addEntity(new MainMenu());

      // Unpause the game
      game.unpause();
    }, 0);
  };

  onAdd() {
    // Set initial volume if game is available
    if (this.game?.masterGain) {
      this.game.masterGain.gain.value = this.currentVolume;
    }

    // Set sprite for positioning
    this.sprite = this.background;

    // Add all UI elements (background first so it's behind everything else)
    this.sprites = [this.background];
    this.addChild(this.titleText);
    this.addChild(this.volumeLabel);
    this.addChild(this.volumePercentage);
    this.addChild(this.volumeSlider);
    this.addChild(this.resumeButton);
    this.addChild(this.mainMenuButton);
    this.addChild(this.instructionText);

    // Position everything
    this.updatePositions();

    // Draw the background
    this.drawBackground();
  }

  private drawBackground(): void {
    if (!this.game) return;

    // Get screen dimensions
    const screenWidth = this.game.renderer.app.renderer.width;
    const screenHeight = this.game.renderer.app.renderer.height;

    // Create a centered background box
    const boxWidth = 500;
    const boxHeight = 400;

    this.background.clear();

    // Draw black background with some transparency
    this.background.rect(-boxWidth / 2, -boxHeight / 2, boxWidth, boxHeight);
    this.background.fill({
      color: 0x000000, // Pure black
      alpha: 0.9,
    });

    // Draw border
    this.background.rect(-boxWidth / 2, -boxHeight / 2, boxWidth, boxHeight);
    this.background.stroke({
      color: Colors.CYAN,
      width: 3,
    });

    // Position background at center of screen
    this.background.position.set(screenWidth / 2, screenHeight / 2);
  }

  private updatePositions(): void {
    if (!this.game) return;

    // Get screen dimensions
    const screenWidth = this.game.renderer.app.renderer.width;
    const screenHeight = this.game.renderer.app.renderer.height;
    const centerX = screenWidth / 2;
    const centerY = screenHeight / 2;

    // Position title (relative to center since background is at center)
    this.titleText.position = V(centerX, centerY - 150);

    // Position volume controls (grouped together)
    const volumeY = centerY - 50;
    this.volumeLabel.position = V(centerX - 180, volumeY);
    this.volumeSlider.setPosition(V(centerX, volumeY));
    this.volumePercentage.position = V(centerX + 180, volumeY);

    // Position buttons with better spacing
    this.resumeButton.setPosition(V(centerX - 110, centerY + 50));
    this.mainMenuButton.setPosition(V(centerX + 110, centerY + 50));

    // Position instruction text
    this.instructionText.position = V(centerX, centerY + 140);
  }
}
